/*
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'./WidgetLoader',
	'../../lib/@waca/core-client/js/core-client/utils/ClassFactory',
	'underscore',
	'./BoardLoaderHelper',
	'../../lib/@waca/core-client/js/core-client/utils/Deferred'
], function(BaseClass, WidgetLoader, ClassFactory,
	_, BoardLoaderHelper, Deferred) {

	/**
	 * The board loader's responsibility is to load a dashboard. The board loader will be used in two main scenarios:
	 *
	 * 1. Loading a board from within the Gemini app (e.g from the homepage)
	 * 2. Loading a board directly from a URL
	 *
	 * @param options
	 *
	 *	boardSpec			The board specification if avaialble, otherwise the spec will be fetched using the board ID
	 *  boardId				The board ID used to fetch the spec
	 *  isAuthoringMode		Set to true if the board is being loaded in authoring mode
	 *  el					The element to render the board layout to
	 *  isLayoutRendered	Set to false if the board's layout hasn't already been rendered
	 *
	 */
	var BoardLoader = BaseClass.extend({

		init: function (options) {
			BoardLoader.inherited('init', this, arguments);
			options = options || {};

			// start - to be removed after done the refactoring
			this.glassContext = options.glassContext;
			this.appSettings = options.appSettings || {};
			this.services = options.services;
			this.ajaxSvc = options.ajaxSvc;
			// end - to be removed after done the refactoring

			this.dashboardApi = options.dashboardApi;

			this.internalCanvas = this.dashboardApi.getFeature('Canvas.internal');

			this.eventRouter = options.eventRouter;

			this.boardSpec = options.boardSpec;
			this.addOnSpec = options.addOnSpec;
			this.specUrl = options.specUrl;

			this.boardModel = options.boardModel;

			this.setDirty = this.boardModel.isUpgraded || false;

			this.pageViewContentNode = options.el;
			this.contentNode = null;
			this.$viewEl = options.$viewEl;
			this.isLayoutRendered = options.isLayoutRendered;

			this._registry = options.widgetRegistry;


			this.gatewayUrl = options.gatewayUrl || '';
			this.cdnUrl = options.cdnUrl || '';
			this.currentVersion = options.currentVersion;
			this.logger = this.dashboardApi.getGlassCoreSvc('.Logger');

			this.stringResources = this.dashboardApi.getDashboardCoreSvc('.StringResources');

			this.extensions = options.extensions;

			this.boardLoaderHelper = new BoardLoaderHelper(this.dashboardApi);

			// loading dfds
			this._canvasControllerDfd = new Deferred();

			this.featureLoader = options.featureLoader;
			this._dashboardController = options.dashboardController;
			this._colorsService = this.dashboardApi.getFeature('Colors');
		},

		getCanvasController: function() {
			return this._canvasControllerDfd.promise;
		},


		/**
		 * Load the board
		 *
		 * @returns Deferred which is resolved once the board is loaded
		 */
		loadBoard: function () {
			this._registerServices();
			return this._initializeTranslationService()
				.then(this._renderLayoutHtml.bind(this))
				.then(this._initBoardController.bind(this))
				.then(this._postDashboardInitialize.bind(this));
		},

		_initializeTranslationService: function() {
			return this.dashboardApi.getDashboardSvc('TranslationService').then(function(translationService) {
				return translationService.initialize( { boardModel: this.boardModel, view: this.$viewEl } );
			}.bind(this));
		},

		_getDashboardFrameNode: function () {
			const parentNode = this.pageViewContentNode;
			let dashboardFrame = parentNode.querySelector('.dashboardFrame');
			if (!dashboardFrame) {
				dashboardFrame = document.createElement('div');
				dashboardFrame.setAttribute('class', 'dashboardFrame');

				const dashboardFrameCentre = document.createElement('div');
				dashboardFrameCentre.setAttribute('class', 'dashboardFrameCentre');

				const dashboardFrameRight = document.createElement('div');
				dashboardFrameRight.setAttribute('class', 'dashboardFrameRight');

				dashboardFrame.appendChild(dashboardFrameCentre);

				dashboardFrame.appendChild(dashboardFrameRight);

				parentNode.appendChild(dashboardFrame);
			}
			return dashboardFrame;
		},

		_getDashboardFrameCentreNode: function () {
			const dashboardFrame = this._getDashboardFrameNode();
			return dashboardFrame.querySelector('.dashboardFrameCentre');
		},

		_setContentNode: function() {
			this.contentNode = this._getDashboardFrameCentreNode();
		},
		/**
		 * Render the layout HTML into the board's DOM element
		 *
		 * TODO: This is a bit awkward, it'd be nice if we could just create the board and if the DOM elements don't exist that
		 * we'd create them on the fly.
		 *
		 * @returns deferred which is resolved once the HTML is rendered
		 */
		_renderLayoutHtml: function () {
			// first set the content node where we want to render the content
			this._setContentNode();

			return this.boardLoaderHelper.loadLayoutExtensions(this.extensions.getLayoutViewExtensions())
				.then(layoutExtensions => {
					this.boardLoaderHelper.registerLayoutExtensions(layoutExtensions);
					var html = this.dashboardApi.getFeature('htmlTemplates').getHtml(this.boardModel.layout, this.boardModel.widgetInstances);
					if(!this.isLayoutRendered) {
						this.contentNode.innerHTML = html;
					}
				});
		},

		/**
		 * Initialize the board controller
		 */
		_initBoardController: function () {
			return ClassFactory
				.loadModule('dashboard-core/js/dashboard/CanvasController')
				.then((CanvasController) => {
					this.widgetLoader = new WidgetLoader({
						// TODO begin to be removed
						glassContext: this.glassContext,
						services: this.services,
						appSettings: this.appSettings,
						// end to be removed

						dashboardApi: this.dashboardApi,
						eventRouter: this.eventRouter,

						widgetRegistry: this._registry,
						loadedWidgets: {},
						boardModel: this.boardModel,
						contentFeatureLoader: this.internalCanvas,
						canvas: this.dashboardApi.getFeature('Canvas')
					});

					this.canvasController = new CanvasController({
						//begin to be removed
						glassContext: this.glassContext,
						services: this.services,
						appSettings: this.appSettings,
						//end to be removed

						dashboardApi: this.dashboardApi,
						eventRouter: this.eventRouter,
						$el: this.$viewEl,
						boardModel: this.boardModel,
						widgetLoader: this.widgetLoader,
						layoutExtensions: this.extensions.getLayoutViewExtensions(),
						boardLoader: this,
						gatewayUrl: this.gatewayUrl,
						cdnUrl: this.cdnUrl,
						contentFeatureLoader: this.internalCanvas
					});

					// TODO - Explore and storyTelling are using this service
					// Should be removed
					this.dashboardApi.registerDashboardSvc('widgetLoader', {
						loadedWidgets: this.widgetLoader.loadedWidgets,
						loadingWidgets: this.widgetLoader.loadingWidgets,
						load: this.widgetLoader.loadWidget.bind(this.widgetLoader),
						unload: this.widgetLoader.unLoadWidget.bind(this.widgetLoader),
						isLoaded: this.widgetLoader.isLoaded.bind(this.widgetLoader),
						isLoading: this.widgetLoader.isLoading.bind(this.widgetLoader),
						getWidget: this.widgetLoader.getWidget.bind(this.widgetLoader)
					});

					return this.canvasController.initialize()
						.then(() => {
							this._canvasControllerDfd.resolve(this.canvasController);
						});
				})
				.catch((err) => {
					this.logger.error(err);
					// don't rethrow, recover
					// TODO: probably rethrowing is a good idea. if you're
					// brave enough and you know what you're doing, make it do that.
				});
		},

		_postDashboardInitialize: function() {
			return this.dashboardApi.getFeature('.LifeCycleManager').invokeLifeCycleHandlers('post:dashboard.initialize');
		},

		_registerServices: function () {

			// TODO - these services must go away
			//Add the board loader services
			const services = this.boardLoaderHelper.createBoardServices({
				boardModel: this.boardModel,
				widgetRegistry: this._registry
			});

			this.smartNamingSvc = services.smartNamingSvc;

		},

		activate: function () {
			if (this._colorsService) {
				this._colorsService.enableTheme();
			}
		},

		deactivate: function () {
			if (this._colorsService) {
				this._colorsService.disableTheme();
			}
		},

		destroyViews: function () {
			this.deactivate();

			// The canvasController and widget loader might not exist if we are loading an invalid dashboard( missing/invalid spec or no permissions)
			if (this.widgetLoader) {
				this.widgetLoader.cleanup();
				this.widgetLoader = null;
			}

			if (this.canvasController) {
				this.canvasController.destroy();
				this.canvasController = null;
			}
		}

	});
	return BoardLoader;

});
